import React, { ReactNode } from 'react';
import { Button, Heading4, Heading5 } from 'is24-corecss';
import { css } from '@emotion/css';
import is24SolvencyWithButton from './is24_solvency_with_button.png';
import is24SolvencyWithButtonMobile from './is24_solvency_with_button_mobile.png';
import { SolvencyInfoResponse } from '@/@types/solvency';
import { Msg, useIntl } from '@/i18n';
import { APP_CONFIG } from '@/config';
import de from '@/i18n/locales/de';

interface ChangeSolvencyChoiceModalProps {
  setSolvencyChoiceModalType: (modal: SolvencyChoiceModalType) => void;
}

export interface SchufaInfo {
  solvencyInfo?: SolvencyInfoResponse;
}

export enum SolvencyChoiceModalType {
  SOLVENCY_CHOICE = 'SOLVENCY_CHOICE',
  SCHUFA_BY_VERIFICATION_CODE = 'SCHUFA_BY_VERIFICATION_CODE',
  IS24_SOLVENCY_PREVIEW = 'IS24_SOLVENCY_PREVIEW',
}

const styles = {
  container: css`
    padding: 16px;
    @media (min-width: 780px) {
      max-width: 650px;
      margin: 0 auto;
    }
  `,
  borderRound: css`
    border-radius: 4px;
  `,
  previewImage: css`
    cursor: pointer;
    max-height: 104px;
    @media (min-width: 669px) {
      max-height: 170px;
      height: 170px;
      max-width: 215px;
    }
  `,
  solvencyBenefitContent: css`
    @media (min-width: 669px) {
      display: flex;
      width: 100%;
      justify-content: space-between;
    }
  `,
  createSolvencyCta: css`
    margin-bottom: 4px;
    margin-top: 32px;
  `,
};

const is24SolvencyUrl = `${process.env.BASE_URL}/bonitaetsauskunft/?cmp_id=10-052075&cmp_name=residential_profile&cmp_position=profile_solvency&cmp_creative=create_solvency_tile`;

const schufaCheckUrl = `${APP_CONFIG.schufaCheckUrl}?cmp_id=10-051402&cmp_name=residential_profile&cmp_position=residential_benefits&cmp_creative=solvency_cta`;

export const SolvencyChoice = ({
  setSolvencyChoiceModalType,
  solvencyInfo,
}: SchufaInfo & ChangeSolvencyChoiceModalProps) => {
  const { t } = useIntl();

  return (
    <div className={styles.container}>
      <Heading4>
        {t({
          id: 'benefits.schufaCheckMieterPlus.solvencyChoice.headline',
        })}
      </Heading4>
      <SolvencyAlreadyBought />
      <Content setSolvencyChoiceModalType={setSolvencyChoiceModalType} solvencyInfo={solvencyInfo} />
    </div>
  );
};

const Content: React.FC<ChangeSolvencyChoiceModalProps & SchufaInfo> = ({
  setSolvencyChoiceModalType,
  solvencyInfo,
}) => {
  return (
    <div className={'grid grid-flex gutter-m padding-bottom-m grid-align-center'}>
      <div className={'grid-item one-whole '}>
        <Is24SolvencyCard setSolvencyChoiceModalType={setSolvencyChoiceModalType} />
      </div>
      <div className={'grid-item one-whole'}>
        <SchufaSolvencyLink solvencyInfo={solvencyInfo} />
      </div>
    </div>
  );
};

export const SolvencyAlreadyBought = () => {
  const { t } = useIntl();
  return (
    <div className={'padding-bottom-m palm-padding-bottom-l'}>
      {t({
        id: 'benefits.schufaCheckMieterPlus.solvencyChoice.alreadyBought.text',
      })}
      <a href={APP_CONFIG.rentProfileUrl}>
        {t({
          id: 'benefits.schufaCheckMieterPlus.solvencyChoice.alreadyBought.link',
          defaultMessage: 'Jetzt einsehen',
        })}
      </a>
    </div>
  );
};

const Is24SolvencyCard: React.FC<ChangeSolvencyChoiceModalProps> = ({ setSolvencyChoiceModalType }) => {
  const { t } = useIntl();

  const showPreview = () => setSolvencyChoiceModalType(SolvencyChoiceModalType.IS24_SOLVENCY_PREVIEW);

  return (
    <div className={`border padding-l ${styles.borderRound} height-full`} id={'is24-solvency-frame'}>
      <div>
        <Heading5 className={'margin-none'}>
          {t({
            id: 'benefits.schufaCheckMieterPlus.solvencyChoice.title',
          })}
        </Heading5>
        <div className={'padding-bottom-l padding-top-xs'}>
          <Msg
            id='benefits.schufaCheckMieterPlus.solvencyChoice.is24SolvencyIncluded'
            defaultMessage='für MieterPlus-Mitglieder inklusive'
            values={{
              b: (children: ReactNode) => <b>{children}</b>,
            }}
          />
        </div>
      </div>
      <div className={styles.solvencyBenefitContent}>
        <Button className={'padding-none palm-margin-bottom-l'} onClick={showPreview} textStyle>
          <img
            id={'is24-solvency-image'}
            className={`${styles.previewImage} palm-hide padding-none`}
            src={is24SolvencyWithButton}
            alt={''}
          />
          <img
            id={'is24-solvency-image-mobile'}
            className={`${styles.previewImage} lap-hide desk-hide`}
            src={is24SolvencyWithButtonMobile}
            alt={''}
          />
        </Button>
        <div
          style={{ width: '63%' }}
          className={'palm-one-whole grid-align-stretch lap-padding-left-s desk-padding-left-s grid-flex'}
        >
          <ul className={'list-check'}>
            {de.benefits.schufaCheckMieterPlus.solvencyChoice.description.map((descriptionPart, index) => (
              <li key={index} className={'padding-bottom-s'}>
                {descriptionPart}
              </li>
            ))}
          </ul>
          <Button
            id={'create-is24-solvency-button'}
            appearance={'secondary'}
            className={`one-whole grid-align-end desk-margin-bottom-xs lap-margin-bottom-xs palm-margin-top-xl ${styles.createSolvencyCta}`}
            size={'small'}
            onClick={() => window.location.assign(is24SolvencyUrl)}
          >
            {t({
              id: 'benefits.schufaCheckMieterPlus.solvencyChoice.button.create',
            })}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const SchufaSolvencyLink: React.FC<SchufaInfo> = ({ solvencyInfo }) => {
  const { t } = useIntl();
  const labelPath = getLabelPath(solvencyInfo);

  return (
    <>
      {labelPath != 'benefits.schufaCheckMieterPlus.solvencyChoice.schufaFullPrice' &&
        t({
          id: `${labelPath}.prefix`,
          defaultMessage: 'Zusätzlich inklusive: ',
        })}
      <a href={schufaCheckUrl}>
        {t(
          {
            id: `${labelPath}.link`,
            defaultMessage: 'SCHUFA-BonitätsCheck erstellen',
          },
          { price: `${solvencyInfo?.price}€` },
        )}
      </a>
    </>
  );
};

const getLabelPath = (solvencyInfo?: SolvencyInfoResponse): string => {
  if (solvencyInfo?.freeflow) {
    return 'benefits.schufaCheckMieterPlus.solvencyChoice.schufaIncluded';
  }

  if (solvencyInfo?.discounted && solvencyInfo.price) {
    return 'benefits.schufaCheckMieterPlus.solvencyChoice.schufaDiscounted';
  }

  if (solvencyInfo?.price) {
    return 'benefits.schufaCheckMieterPlus.solvencyChoice.schufaFullPrice';
  }

  return 'benefits.schufaCheckMieterPlus.solvencyChoice.schufaIncluded';
};
